import cloneDeep from 'clone-deep'
import type { IContractorOriginSection } from 'config'
import type { InputType } from 'config/input.type.constants'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { updateContractorApplication } from 'services/apis'
import { FormTableHeader } from 'stories/components'
import { InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { validateInputRecord } from '../validateInputRecord'

type CapabilityStatementProps = {
  inputs: Record<string, InputType>
  sections?: IContractorOriginSection[]
  setInputs: (v: Record<string, InputType> | ((prev: Record<string, InputType>) => Record<string, InputType>)) => void
  onStatusChange: (ok: boolean) => void
  canEdit: boolean
  showHistory: (key: string, headers?: Array<FormTableHeader>) => void
  setLoading: (v: boolean) => void
}

export function CapabilityStatement({
  inputs,
  sections = [],
  setInputs,
  onStatusChange,
  canEdit,
  showHistory,
  setLoading,
}: CapabilityStatementProps) {
  const { no: noParam } = useParams() as { no: string }
  const no = Number(noParam)
  const [fieldValueChanged, setFieldValueChanged] = useState(false)

  const onChangeCapabilityInfo = (key: string, value: string) => {
    setFieldValueChanged(true)
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    const { data, success } = validateInputRecord(newInputs)
    setInputs(data)
    onStatusChange(success)
  }

  const onBlurCapabilityInfo = async (key: string) => {
    if (!fieldValueChanged) return
    setFieldValueChanged(false)
    const input: any = inputs[key]
    if (input.error && input.error.length > 0) return
    setLoading(true)
    await updateContractorApplication(no, { [key]: input.value } as any)
    setLoading(false)
  }

  const renderField = (key: string) => {
    const input = { ...inputs[key], disabled: !canEdit }
    return (
      <div className="input md:col-span-1" key={key}>
        <RenderInput
          input={input}
          Key={key}
          onChange={onChangeCapabilityInfo}
          showHistory={showHistory}
          onBlur={onBlurCapabilityInfo}
        />
      </div>
    )
  }

  const sectionFieldKeys = useMemo(() => {
    const keys = new Set<string>()
    sections.forEach((s) => s.fields.forEach((k) => keys.add(k)))
    return keys
  }, [sections])

  const freeKeys = Object.keys(inputs).filter((key) => !sectionFieldKeys.has(key))
  const showHeaders = sections.filter((s) => s.fields.some((k) => inputs[k])).length > 1

  return (
    <div className="mb-3">
      {sections.map((section) => {
        const visibleFields = section.fields.filter((key) => inputs[key])
        if (!visibleFields.length) return null
        return (
          <div key={section.id} className="mb-8">
            {(showHeaders || section.title) && section.title && (
              <div className="mb-6 pb-1">
                <RenderInput
                  Key={`section-${section.id}`}
                  input={{ inputType: 'section', title: section.title }}
                  onChange={() => {}}
                />
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2 grid-cols-1">{visibleFields.map(renderField)}</div>
          </div>
        )
      })}
      {!!freeKeys.length && (
        <div className="grid gap-4 md:grid-cols-2 grid-cols-1">{freeKeys.map(renderField)}</div>
      )}
    </div>
  )
}
